import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';

import type { SerieLayer } from '@/api/generated/schemas';
import { LayerState } from '@/api/generated/schemas';
import {
  getGetVenuaPlannerCalendarTermsQueryKey,
  getGetVenuaPlannerSerieDetailQueryKey,
  getGetVenuaPlannerSerieEconomicsQueryKey,
  getGetVenuaPlannerTermDetailQueryKey,
  useChangeVenuaPlannerSerieLayerState,
  useChangeVenuaPlannerTermLayerState,
} from '@/api/generated/venua-planner-planning/venua-planner-planning';
import { toast } from '@/shared/ui';
import { serverErrorDetail } from '@/shared/lib/problemDetails';
import { venuaPlannerQueryKeys } from '../../venuaPlannerApi';
import { toSerieLayerKey } from './serieLayers';

export interface TermScope {
  productId: string;
  serieId: string;
  termId: string;
}

export interface SerieScope {
  productId: string;
  serieId: string;
}

/**
 * Invaliduje dopravní schéma seriálu **i všech jeho termínů**.
 *
 * Oba rozsahy najednou, protože si čtou navzájem do talíře: seriálová matice se skládá z řádků termínů
 * (stav, kapacita, ruční ceny, přiřazená trasa), zatímco termín ukazuje sdílenou definici seriálu.
 * Kdyby se invalidoval jen ten měněný, druhý — typicky už otevřená záložka za oknem termínu — by dál
 * ukazoval a rozhodoval se podle dat, která už neplatí.
 *
 * Klíčem je URL, takže se matchuje předponou; oba tvary končí na `/transport-scheme`, což zároveň
 * vynechá nekešovaný `swap-impact`.
 */
export function invalidateTransportSchemes(
  queryClient: QueryClient,
  scope: SerieScope
): Promise<void> {
  const prefix = `/api/venua-planner/products/${scope.productId}/series/${scope.serieId}`;
  return queryClient.invalidateQueries({
    predicate: (query) => {
      const url = query.queryKey[0];
      return typeof url === 'string' && url.startsWith(prefix) && url.endsWith('/transport-scheme');
    },
  });
}

/**
 * Invaliduje kalkulaci seriálu **i všech jeho termínů** a odvozenou ekonomiku.
 *
 * Do nákladu termínu vstupuje i doprava, takže každá změna cen ticketů nebo přepnutí sdílená ↔ vlastní
 * definice mění marži. Bez invalidace by Yield Management i tabulka termínů ukazovaly starou marži až
 * do vypršení cache.
 *
 * Termínová varianta se matchuje předponou URL — kalkulace se otevírá i za oknem termínu a scope, který
 * se zrovna needituje, by jinak zůstal na neplatných číslech.
 */
export function invalidateCalculationScopes(queryClient: QueryClient, scope: SerieScope): void {
  const prefix = `/api/venua-planner/products/${scope.productId}/series/${scope.serieId}`;
  void queryClient.invalidateQueries({
    predicate: (query) => {
      const url = query.queryKey[0];
      return typeof url === 'string' && url.startsWith(prefix) && url.endsWith('/calculation');
    },
  });
  void queryClient.invalidateQueries({
    queryKey: getGetVenuaPlannerSerieEconomicsQueryKey(scope.productId, scope.serieId),
  });
}

/**
 * Invaliduje pohledy nesoucí stavy vrstev termínu: detail termínu, detail seriálu (tabulka termínů),
 * plánovací strom, kalendář a dopravní schéma (matice v záložce Doprava jede po termínech).
 */
export function invalidateTermScopes(queryClient: QueryClient, scope: TermScope): void {
  void queryClient.invalidateQueries({
    queryKey: getGetVenuaPlannerTermDetailQueryKey(scope.productId, scope.serieId, scope.termId),
  });
  void queryClient.invalidateQueries({
    queryKey: getGetVenuaPlannerSerieDetailQueryKey(scope.productId, scope.serieId),
  });
  void queryClient.invalidateQueries({ queryKey: venuaPlannerQueryKeys.tree() });
  void queryClient.invalidateQueries({
    queryKey: [getGetVenuaPlannerCalendarTermsQueryKey()[0]],
  });
  void invalidateTransportSchemes(queryClient, scope);
}

/** Invaliduje pohledy nesoucí stavy vrstev seriálu: detail seriálu, plánovací strom a kalendář. */
export function invalidateSerieScopes(queryClient: QueryClient, scope: SerieScope): void {
  void queryClient.invalidateQueries({
    queryKey: getGetVenuaPlannerSerieDetailQueryKey(scope.productId, scope.serieId),
  });
  void queryClient.invalidateQueries({ queryKey: venuaPlannerQueryKeys.tree() });
  void queryClient.invalidateQueries({
    queryKey: [getGetVenuaPlannerCalendarTermsQueryKey()[0]],
  });
  void invalidateTransportSchemes(queryClient, scope);
}

interface ChangeStateResult {
  changedLayers: SerieLayer[];
  highestApprovedLayer?: SerieLayer | null;
  stoppedAtLayer?: SerieLayer | null;
  stoppedReason?: string | null;
  /** Počet termínů, které se při kaskádovém schválení neschválily (nesplnily validaci). Jen scope seriálu. */
  skippedTermCount?: number;
}

/**
 * Sdílená obsluha odpovědi stavově-změnové mutace. Schvalování je **best-effort**: HTTP 200 neznamená,
 * že prošlo všechno – při `stoppedAtLayer` se zobrazí varování. Ostatní přechody (Připravit / Vrátit)
 * jsou single-layer; jejich reject přijde jako výjimka (zachytí `catch`).
 */
function useToastReporter() {
  const { t } = useTranslation('venua-planner');
  const layerLabel = useCallback(
    (layer: SerieLayer) => t(`serieDetail.layers.${toSerieLayerKey(layer)}`),
    [t]
  );

  return useCallback(
    (targetState: LayerState, result: ChangeStateResult): boolean => {
      if (targetState === LayerState.Approved) {
        if (result.stoppedAtLayer) {
          toast({
            severity: 'warning',
            title: t('approve.toast.partialTitle'),
            description: result.highestApprovedLayer
              ? t('approve.toast.partial', {
                  layer: layerLabel(result.highestApprovedLayer),
                  reason: result.stoppedReason ?? t('approve.toast.unknownReason'),
                })
              : t('approve.toast.none', {
                  reason: result.stoppedReason ?? t('approve.toast.unknownReason'),
                }),
          });
          return false;
        }
        // Schválení vrstvy proběhlo, ale kaskáda do některých termínů ne (nesplnily validaci) – best-effort.
        if (result.skippedTermCount && result.skippedTermCount > 0) {
          toast({
            severity: 'warning',
            title: t('approve.toast.partialTitle'),
            description: t('approve.toast.partialTerms', { count: result.skippedTermCount }),
          });
          return true;
        }
        toast({
          severity: 'success',
          description: result.highestApprovedLayer
            ? t('approve.toast.done', { layer: layerLabel(result.highestApprovedLayer) })
            : t('approve.toast.doneNone'),
        });
        return true;
      }

      toast({
        severity: 'success',
        description:
          targetState === LayerState.Prepared
            ? t('layerState.toast.prepared')
            : t('layerState.toast.draft'),
      });
      return true;
    },
    [t, layerLabel]
  );
}

/**
 * Chybový toast podle akce: schvalování má vlastní hlavičku, ostatní přechody (Připravit / Vrátit do přípravy)
 * jinou – jinak by se u vracení schválení ukázalo zavádějící „Schválení se nezdařilo“. Popis přebírá konkrétní
 * důvod z backendu (např. „Vrstvu Termíny nelze vrátit, když je vybraná doprava…“), jinak obecný fallback.
 */
function useErrorToast() {
  const { t } = useTranslation('venua-planner');
  return useCallback(
    (targetState: LayerState, error: unknown) => {
      const isApprove = targetState === LayerState.Approved;
      toast({
        severity: 'error',
        title: isApprove ? t('approve.toast.errorTitle') : t('layerState.toast.errorTitle'),
        description:
          serverErrorDetail(error) ??
          (isApprove ? t('approve.toast.error') : t('layerState.toast.error')),
      });
    },
    [t]
  );
}

/** Stavově-změnová mutace termínu (Připravit / Vrátit / Schválit / Vrátit schválení) přes jeden endpoint. */
export function useChangeTermLayerState(scope: TermScope) {
  const queryClient = useQueryClient();
  const mutation = useChangeVenuaPlannerTermLayerState();
  const report = useToastReporter();
  const reportError = useErrorToast();

  const { productId, serieId, termId } = scope;

  const changeState = useCallback(
    async (layer: SerieLayer, targetState: LayerState): Promise<boolean> => {
      try {
        const response = await mutation.mutateAsync({
          productId,
          serieId,
          termId,
          data: { layer, targetState },
        });
        invalidateTermScopes(queryClient, { productId, serieId, termId });
        return report(targetState, response);
      } catch (error) {
        reportError(targetState, error);
        return false;
      }
    },
    [mutation, queryClient, productId, serieId, termId, report, reportError]
  );

  return { changeState, isPending: mutation.isPending };
}

/** Stavově-změnová mutace seriálu (Připravit / Vrátit / Schválit / Vrátit schválení) přes jeden endpoint. */
export function useChangeSerieLayerState(scope: SerieScope) {
  const queryClient = useQueryClient();
  const mutation = useChangeVenuaPlannerSerieLayerState();
  const report = useToastReporter();
  const reportError = useErrorToast();

  const { productId, serieId } = scope;

  const changeState = useCallback(
    async (layer: SerieLayer, targetState: LayerState): Promise<boolean> => {
      try {
        const response = await mutation.mutateAsync({
          productId,
          serieId,
          data: { layer, targetState },
        });
        invalidateSerieScopes(queryClient, { productId, serieId });
        return report(targetState, response);
      } catch (error) {
        reportError(targetState, error);
        return false;
      }
    },
    [mutation, queryClient, productId, serieId, report, reportError]
  );

  return { changeState, isPending: mutation.isPending };
}
